package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.WebImportRouteStrings
import com.milta.core.localization.unplacedStops
import com.milta.core.localization.workloadGapText
import com.milta.core.localization.zoneText
import com.milta.feature.delivery.domain.entity.AddressSuggestion
import com.milta.feature.optimizationquota.domain.entity.OptimizationQuota
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.feature.optimizationquota.presentation.quotaReachedMessage
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.OptimizationSkippedReason
import com.milta.feature.route.domain.entity.GeocodeStatus
import com.milta.feature.route.domain.entity.RouteBatchDriverProposal
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.route.presentation.RouteBatchBalance
import com.milta.feature.route.presentation.WebImportRouteIntent
import com.milta.feature.route.presentation.WorkloadBalance
import com.milta.feature.route.presentation.WebImportRouteState
import com.milta.feature.team.domain.entity.Driver
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** Only the first 4 stops of each driver's route are previewed on its card — matches the ticket's
 * mockup intent, avoids an unbounded list for a large batch. */
private const val MAX_PREVIEW_STOPS = 4

/**
 * Écran 3 (MIL-51) — répartition du batch avant envoi. One card per driver (avatar/initials, name,
 * distance/duration-or-"non calculée", stops count pastille, first [MAX_PREVIEW_STOPS] stops), then
 * a single "Valider et envoyer les tournées" action (`POST /route-batches/{id}/dispatch`), above
 * "Ré-optimiser" with the optimization counter (MIL-97, E2). Mirrors the mobile
 * `RouteBatchPreviewScreen`'s `DriverProposalCard` (shared/commonMain), reimplemented with Compose
 * HTML primitives (this module targets Kotlin/Wasm-JS, not Compose Multiplatform Material) rather
 * than reused directly.
 */
@Composable
internal fun WebRouteBatchResultScreen(
    state: WebImportRouteState,
    strings: WebImportRouteStrings,
    /** MIL-97 — the Enterprise's own optimization counter; `null` until read (no counter shown). */
    quota: OptimizationQuota? = null,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    val result = state.batchResult ?: return
    val quotaStrings = LocalStrings.current.optimizationQuota

    val dispatchError = state.submitError
    Div {
        if (dispatchError != null) {
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                InlineBanner(
                    kind = InlineBannerKind.Error,
                    text = if (dispatchError == "optimization_quota_exceeded") {
                        quotaStrings.quotaReachedMessage(state.quotaReachedResetAt)
                    } else {
                        mapDispatchError(dispatchError, strings)
                    },
                )
            }
        }

        // MIL-97 — E1 "épuisé au clic" (R11): optimization asked, skipped for lack of quota, the
        // batch exists in the entered order. E2: a batch sent without optimization at 0.
        if (result.optimizationSkippedReason == OptimizationSkippedReason.QuotaExceeded) {
            QuotaWarnBanner(
                title = quotaStrings.quotaReachedMessage(quota?.resetAt),
                message = quotaStrings.batchCreatedInEnteredOrder,
            )
        } else if (!result.optimized && quota?.isExhausted == true) {
            QuotaInfoBanner(title = quotaStrings.batchNotOptimizedTitle, message = quotaStrings.batchNotOptimizedMessage)
        }

        Div(
            attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "16px") } },
        ) { Text(buildResultHeader(result, strings)) }

        // MIL-99 — after the split: stops RoutaR couldn't place, put at the end of their route. No
        // snackbar on the web dashboard, the existing inline banner carries this short message.
        // Retour 28/10 (point 6) — addresses to correct: red, like the mobile snackbar.
        if (result.unlocatedDeliveryCount > 0) {
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                InlineBanner(kind = InlineBannerKind.Error, text = LocalStrings.current.unlocatedDelivery.unplacedStops(result.unlocatedDeliveryCount))
            }
        }

        // Retour 26/10 (demande 3, F2) — is the working time balanced between drivers?
        RouteBatchBalance.workloadBalance(result.drivers)?.let { balance ->
            Div(attrs = { style { property("margin-bottom", "14px") } }) { WorkloadBalanceBanner(balance) }
        }

        result.drivers.forEach { proposal ->
            DriverResultCard(
                proposal = proposal,
                zoneCities = RouteBatchBalance.zoneCities(proposal.deliveries.map { it.address }),
                durationRatio = RouteBatchBalance.durationRatio(proposal, result.drivers),
                driver = state.drivers.firstOrNull { it.id == proposal.driverAccountId },
                stops = state.batchStopsByRouteId[proposal.routeId].orEmpty(),
                isLoadingStops = state.isLoadingBatchStops,
                hasStopsLoadError = proposal.routeId in state.batchStopsFailedRouteIds,
                strings = strings,
                // The batch isn't sent yet: the creator can still correct an unlocated stop.
                canFixAddress = !state.isDispatchingBatch && dispatchError != "already_dispatched",
                fixingStopIds = state.fixingStopIds,
                onFixAddress = { deliveryId, suggestion ->
                    onIntent(WebImportRouteIntent.FixBatchStopAddress(proposal.routeId, deliveryId, suggestion))
                },
            )
            Div(attrs = { style { height(12.px) } })
        }

        // Mirrors WebVerifyAssignRouteScreen's SubmitButton: once dispatch has terminally failed with
        // "already_dispatched" (409 — the batch is already sent, retrying is a no-op that would just
        // re-show the same banner), the CTA stays disabled instead of inviting another click.
        val dispatchDisabled = state.isDispatchingBatch || state.isReoptimizingBatch || dispatchError == "already_dispatched"
        // MIL-97 — E2, new "Ré-optimiser" (mobile RouteBatchPreviewScreen's, web secondary style):
        // counter shown, disabled in advance at 0 (R10); "Valider et envoyer" stays available (R6).
        val showReoptimize = state.routarEnabled
        val reoptimizeDisabled = state.isReoptimizingBatch || state.isDispatchingBatch ||
            dispatchError == "already_dispatched" || quota?.isExhausted == true
        // Retour 26/10 — stacked: "Valider et envoyer" (main) above "Ré-optimiser" (secondary).
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
            Button(
                attrs = {
                    if (!dispatchDisabled) onClick { onIntent(WebImportRouteIntent.ValidateAndSend) }
                    style {
                        gradientButtonStyle(this)
                        width(100.percent)
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("gap", "8px")
                        if (dispatchDisabled) {
                            property("opacity", "0.5")
                            property("cursor", "not-allowed")
                            property("box-shadow", "none")
                        }
                    }
                },
            ) {
                WebIcon(WebIconName.Send, size = 18)
                Text(strings.resultDispatchButton)
            }
            if (showReoptimize) {
                Button(
                    attrs = {
                        if (!reoptimizeDisabled) onClick { onIntent(WebImportRouteIntent.ReoptimizeBatch) }
                        style {
                            secondaryButtonStyle(this)
                            width(100.percent)
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("gap", "8px")
                            property("font-size", "14px"); property("padding", "12px 16px"); property("border-radius", "10px")
                            if (reoptimizeDisabled) {
                                property("opacity", "0.5")
                                property("cursor", "not-allowed")
                            }
                        }
                    },
                ) {
                    if (state.isReoptimizingBatch) {
                        WebSpinner(size = 14, strokeWidth = 2)
                    } else {
                        WebIcon(WebIconName.Refresh, size = 18)
                        Text(quotaStrings.reoptimizeAction)
                    }
                }
            }
        }
        if (showReoptimize && quota != null && quota.isExhausted) {
            QuotaHint(quotaStrings.reoptimizeUnavailableWeb.fillPlaceholders("date" to OptimizationQuotaFormat.dayMonth(quota.resetAt)))
        }
    }
}

private fun buildResultHeader(result: RouteBatchResult, strings: WebImportRouteStrings): String {
    val driverWord = if (result.totalDrivers == 1) strings.resultDriverSingularSuffix else strings.resultDriverPluralSuffix
    return "${result.totalDeliveries} ${strings.resultHeaderMiddle} ${result.totalDrivers} $driverWord"
}

@Composable
private fun mapDispatchError(code: String, strings: WebImportRouteStrings): String = when (code) {
    "already_dispatched" -> strings.alreadyDispatchedBanner
    "route_not_found" -> strings.routeNotFoundError
    "optimization_unavailable" -> strings.submitOptimizationUnavailableError
    // MIL-99 — "Corriger l'adresse" failures (403 not the creator / already sent, 400 validation).
    "forbidden" -> LocalStrings.current.route.errorForbidden
    "validation_error" -> strings.validationErrorGeneric
    else -> strings.submitServerError
}

/** Retour 26/10 (demande 3, F2) — green when balanced, orange with the gap otherwise. */
@Composable
private fun WorkloadBalanceBanner(balance: WorkloadBalance) {
    val strings = LocalStrings.current.routeCreation
    val balanced = balance is WorkloadBalance.Balanced
    val ink = if (balanced) WebSuccessInk else WebWarning
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                property("background", if (balanced) WebSuccessBg else WebWarningBg)
                property("border-radius", "10px"); padding(12.px, 14.px)
                property("font-size", "13px"); property("font-weight", "600"); property("color", ink)
            }
        },
    ) {
        WebIcon(if (balanced) WebIconName.CheckCircle else WebIconName.WarningAmber, size = 18, color = ink)
        Text(
            when (balance) {
                WorkloadBalance.Balanced -> strings.workloadBalanced
                is WorkloadBalance.Unbalanced -> strings.workloadGapText(RouteBatchBalance.formatDuration(balance.gapMinutes))
            },
        )
    }
}

@Composable
private fun DriverResultCard(
    proposal: RouteBatchDriverProposal,
    /** Demande 3 (F1) — the 2 main cities of the driver's stops, empty when unknown. */
    zoneCities: List<String>,
    /** Demande 3 (F3) — duration / longest duration, null when unknown or 0. */
    durationRatio: Float?,
    driver: Driver?,
    stops: List<Delivery>,
    isLoadingStops: Boolean,
    hasStopsLoadError: Boolean,
    strings: WebImportRouteStrings,
    canFixAddress: Boolean,
    fixingStopIds: Set<String>,
    onFixAddress: (deliveryId: String, suggestion: AddressSuggestion) -> Unit,
) {
    // MIL-99 — stop whose "Corriger l'adresse" field is open (one at a time per card).
    var editingStopId by remember { mutableStateOf<String?>(null) }
    var fixQuery by remember(editingStopId) { mutableStateOf("") }
    Div(
        attrs = { style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); padding(16.px) } },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "12px") } }) {
            Div(
                attrs = {
                    style {
                        width(40.px); height(40.px); property("border-radius", "10px"); property("flex-shrink", "0")
                        property("background", WebActiveTint)
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("color", WebPrimary); property("font-size", "13.5px"); property("font-weight", "700")
                    }
                },
            ) { Text(driverInitials(driver)) }

            Div(attrs = { style { property("flex", "1") } }) {
                Div(
                    attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } },
                ) { Text(driver?.let { "${it.firstName} ${it.lastName}" } ?: proposal.driverAccountId) }
                // Retour 26/10 (demande 3, F1) — "Zone Wavre, Ottignies", not for a driver without stops.
                if (proposal.deliveryCount > 0 && zoneCities.isNotEmpty()) {
                    Div(
                        attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "2px") } },
                    ) { Text(LocalStrings.current.routeCreation.zoneText(zoneCities)) }
                }
            }

            Div(
                attrs = {
                    style {
                        property("background", WebActiveTint); property("border-radius", "999px")
                        padding(4.px, 10.px)
                        property("font-size", "11px"); property("font-weight", "700"); property("color", WebPrimary)
                    }
                },
            ) { Text("${proposal.deliveryCount} ${strings.resultStopsChipSuffix}") }

            val toReviewCount = proposal.deliveries.count { it.geocodeStatus != GeocodeStatus.Geocoded }
            if (toReviewCount > 0) {
                Div(
                    attrs = {
                        style {
                            property("background", WebWarningBg); property("border-radius", "999px")
                            padding(4.px, 10.px)
                            property("font-size", "11px"); property("font-weight", "700"); property("color", WebWarning)
                        }
                    },
                ) { Text("$toReviewCount ${LocalStrings.current.route.routeBatchAddressesToReviewTag}") }
            }
        }

        Div(attrs = { style { height(12.px) } })

        Div(attrs = { style { property("display", "flex"); property("gap", "24px"); property("padding-left", "52px") } }) {
            MetricColumn(
                label = strings.resultDistanceLabel,
                value = proposal.distanceKm?.let { "${formatOneDecimal(it)} km" } ?: strings.resultMetricNotCalculatedLabel,
            )
            MetricColumn(
                label = strings.resultDurationLabel,
                value = proposal.durationMinutes?.let { formatDuration(it) } ?: strings.resultMetricNotCalculatedLabel,
            )
        }

        // Retour 26/10 (demande 3, F3) — duration relative to the longest one, same accent for all.
        if (durationRatio != null) {
            Div(attrs = { style { property("margin", "10px 0 0 52px"); height(6.px); property("border-radius", "999px"); property("background", WebActiveTint) } }) {
                Div(attrs = { style { height(6.px); property("width", "${durationRatio * 100}%"); property("border-radius", "999px"); property("background", WebPrimary) } })
            }
        }

        Div(attrs = { style { height(10.px) } })

        Div(attrs = { style { property("padding-left", "52px"); property("display", "flex"); property("flex-direction", "column"); property("gap", "4px") } }) {
            if (isLoadingStops) {
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) { Text("…") }
            } else if (hasStopsLoadError) {
                // Distinguishes "the fetch failed" from "this route genuinely has few/no stops" —
                // the stops-count pill and metrics above come from the batch-create response and
                // stay correct regardless, only this preview list is affected.
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) {
                    Text(strings.resultStopsLoadError)
                }
            } else {
                // MIL-99 — unlocated stops come last (backend sortOrder), usually past the preview:
                // they're always listed too, so the creator can correct them before sending.
                val shownStops = stops.take(MAX_PREVIEW_STOPS) +
                    stops.drop(MAX_PREVIEW_STOPS).filter { it.geocodeStatus != GeocodeStatus.Geocoded }
                shownStops.forEach { stop ->
                    Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "8px") } }) {
                        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextPrimary) } }) {
                            Text("${stop.recipientName} — ${stop.address}")
                        }
                        if (stop.geocodeStatus != GeocodeStatus.Geocoded) {
                            Div(attrs = { style { property("font-size", "11px"); property("font-weight", "600"); property("color", WebWarning); property("flex-shrink", "0") } }) {
                                Text(LocalStrings.current.common.addressNeedsReviewBadge)
                            }
                            if (canFixAddress && editingStopId != stop.id) {
                                // Same look as Écran 2's "réessayer la localisation" link (RetryGeocodeLink).
                                Div(
                                    attrs = {
                                        if (stop.id !in fixingStopIds) onClick { editingStopId = stop.id }
                                        style {
                                            property("font-size", "11.5px"); property("font-weight", "700"); property("color", WebPrimary)
                                            property("flex-shrink", "0")
                                            property("cursor", if (stop.id in fixingStopIds) "wait" else "pointer")
                                        }
                                    },
                                ) { Text(if (stop.id in fixingStopIds) "…" else LocalStrings.current.unlocatedDelivery.fixAddressAction) }
                            }
                        }
                    }
                    if (canFixAddress && editingStopId == stop.id) {
                        Div(attrs = { style { property("margin", "4px 0 6px 0") } }) {
                            AddressAutocompleteInput(
                                value = fixQuery,
                                onChange = { fixQuery = it },
                                onSuggestionPicked = { suggestion ->
                                    editingStopId = null
                                    onFixAddress(stop.id, suggestion)
                                },
                                placeholderText = LocalStrings.current.loadingAddress.webSearchPlaceholder,
                            )
                        }
                    }
                }
                // Based on the actually fetched [stops], not `proposal.deliveryCount` — the latter
                // comes from the batch-create response and would overstate "remaining" whenever
                // fewer stops were fetched than the route actually has.
                val remaining = stops.size - shownStops.size
                if (remaining > 0) {
                    Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "600"); property("color", WebTextSecondary) } }) {
                        Text(strings.resultMoreStopsTemplate.replace("%d", remaining.toString()))
                    }
                }
            }
        }
    }
}

@Composable
private fun MetricColumn(label: String, value: String) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column") } }) {
        Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(value) }
        Div(attrs = { style { property("font-size", "11.5px"); property("color", WebTextSecondary) } }) { Text(label) }
    }
}

private fun driverInitials(driver: Driver?): String {
    if (driver == null) return "?"
    val first = driver.firstName.firstOrNull()?.uppercaseChar() ?: ' '
    val last = driver.lastName.firstOrNull()?.uppercaseChar() ?: ' '
    return "$first$last".trim()
}

// String.format/"%.1f" is JVM-only — not available on Kotlin/Wasm-JS (this module's target). Mirrors
// RouteBatchPreviewScreen's own formatOneDecimal (shared/commonMain), reimplemented locally since
// that one is `private` to its file.
private fun formatOneDecimal(value: Double): String {
    val tenths = kotlin.math.round(value * 10).toLong()
    return "${tenths / 10}.${kotlin.math.abs(tenths % 10)}"
}

private fun formatDuration(minutes: Int): String {
    val h = minutes / 60
    val m = minutes % 60
    return if (h > 0) "${h}h${m.toString().padStart(2, '0')}" else "${m} min"
}
